import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, useTheme, useTenant } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';
import { 
  Sun, Moon, ArrowLeft, QrCode, Check, X, Camera, Keyboard, 
  Star, AlertCircle, RefreshCw, VideoOff, Smartphone,
  Calendar, Users, Ticket, ChevronDown
} from 'lucide-react';
import SenArtisteLogo from '../components/SenArtisteLogo';

const TicketScanner = () => {
  const { artistSlug } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { tenant, fetchTenant } = useTenant();
  
  // Event selection
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(true);
  
  // Scanner state
  const [scanMode, setScanMode] = useState('camera');
  const [manualTicketNumber, setManualTicketNumber] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  
  // Stats for current session
  const [scanStats, setScanStats] = useState({ valid: 0, invalid: 0, total: 0 });
  
  // Quick mode - auto return to scanning
  const [quickMode, setQuickMode] = useState(true);
  const quickModeTimeoutRef = useRef(null);
  
  const html5QrCodeRef = useRef(null);
  const isProcessingRef = useRef(false);
  
  // Audio context for sound feedback
  const audioContextRef = useRef(null);
  
  // Initialize audio context on first user interaction
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  };
  
  // Play success sound (pleasant chime - two ascending tones)
  const playSuccessSound = () => {
    try {
      const ctx = initAudioContext();
      const now = ctx.currentTime;
      
      // First tone
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.frequency.value = 880; // A5
      osc1.type = 'sine';
      gain1.gain.setValueAtTime(0.3, now);
      gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
      osc1.start(now);
      osc1.stop(now + 0.15);
      
      // Second tone (higher)
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.frequency.value = 1320; // E6
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(0.3, now + 0.1);
      gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      osc2.start(now + 0.1);
      osc2.stop(now + 0.3);
    } catch (e) {
      console.log('Audio not supported');
    }
  };
  
  // Play error sound (low buzz - descending tones)
  const playErrorSound = () => {
    try {
      const ctx = initAudioContext();
      const now = ctx.currentTime;
      
      // First tone
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.frequency.value = 400; // G4
      osc1.type = 'square';
      gain1.gain.setValueAtTime(0.2, now);
      gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
      osc1.start(now);
      osc1.stop(now + 0.15);
      
      // Second tone (lower)
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.frequency.value = 250; // ~B3
      osc2.type = 'square';
      gain2.gain.setValueAtTime(0.2, now + 0.12);
      gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
      osc2.start(now + 0.12);
      osc2.stop(now + 0.35);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  // Check authentication
  useEffect(() => {
    if (!user || !['ARTISTE', 'MANAGER', 'SCANNER', 'SUPER_ADMIN'].includes(user.role)) {
      navigate(`/${artistSlug}/login`);
      return;
    }
    
    // SCANNER peut uniquement accéder aux événements de SON artiste
    if (user.role === 'SCANNER' && user.artistSlug && user.artistSlug !== artistSlug) {
      toast.error('Vous n\'avez accès qu\'aux événements de votre artiste');
      navigate(`/${user.artistSlug}/scanner`);
      return;
    }
    
    fetchTenant(artistSlug);
    loadEvents();
  }, [user, artistSlug]);

  // Load events for this artist
  const loadEvents = async () => {
    try {
      setLoadingEvents(true);
      const response = await api.get(`/artists/public/${artistSlug}`);
      const artistEvents = response.data.events || [];
      
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      
      // Tous les rôles voient les événements présents et futurs
      const relevantEvents = artistEvents.filter(e => {
        const eventDate = new Date(e.date || e.dateTime);
        return eventDate >= todayStart;
      });
      setEvents(relevantEvents.length > 0 ? relevantEvents : artistEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Erreur lors du chargement des événements');
    } finally {
      setLoadingEvents(false);
    }
  };

  // Get available cameras
  useEffect(() => {
    if (selectedEvent) {
      Html5Qrcode.getCameras()
        .then(devices => {
          if (devices && devices.length) {
            setCameras(devices);
            const backCamera = devices.find(d => 
              d.label.toLowerCase().includes('back') || 
              d.label.toLowerCase().includes('arrière') ||
              d.label.toLowerCase().includes('rear')
            );
            setSelectedCamera(backCamera?.id || devices[0].id);
          }
        })
        .catch(err => {
          console.error('Error getting cameras:', err);
          setCameraError('Impossible d\'accéder aux caméras');
        });
    }
  }, [selectedEvent]);

  // Initialize/cleanup scanner
  useEffect(() => {
    let isMounted = true;
    let startTimeout = null;
    
    const initScanner = async () => {
      if (selectedEvent && scanMode === 'camera' && selectedCamera && !validationResult) {
        // Wait a bit to ensure previous scanner is fully stopped
        startTimeout = setTimeout(async () => {
          if (isMounted) {
            await startScanner();
          }
        }, 200);
      }
    };
    
    initScanner();
    
    return () => {
      isMounted = false;
      if (startTimeout) {
        clearTimeout(startTimeout);
      }
      stopScanner();
      if (quickModeTimeoutRef.current) {
        clearTimeout(quickModeTimeoutRef.current);
      }
    };
  }, [selectedEvent, scanMode, selectedCamera, validationResult]);

  const startScanner = async () => {
    try {
      setCameraError(null);
      setCameraReady(false);
      
      // Force cleanup of any existing scanner
      if (html5QrCodeRef.current) {
        try {
          if (html5QrCodeRef.current.isScanning) {
            await html5QrCodeRef.current.stop();
          }
        } catch (e) {
          console.log('Cleanup warning:', e);
        }
        html5QrCodeRef.current = null;
      }

      // Wait for DOM and camera release
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const container = document.getElementById('qr-reader-container');
      if (!container) {
        console.log('Container not found, retrying...');
        return;
      }
      
      // Clear container content to prevent duplicates
      container.innerHTML = '';

      const html5QrCode = new Html5Qrcode('qr-reader-container');
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 15,
        qrbox: { width: 280, height: 280 },
        aspectRatio: 1.0,
        disableFlip: false,
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true
        }
      };

      await html5QrCode.start(
        selectedCamera,
        config,
        onScanSuccess,
        onScanError
      );

      setCameraReady(true);

    } catch (err) {
      console.error('Scanner start error:', err);
      handleCameraError(err);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        const scanner = html5QrCodeRef.current;
        // Check if scanner is running before stopping
        if (scanner.isScanning) {
          await scanner.stop();
        }
      } catch (e) {
        console.log('Stop scanner warning:', e);
      } finally {
        html5QrCodeRef.current = null;
      }
    }
    setCameraReady(false);
  };

  const handleCameraError = (err) => {
    let errorMessage = 'Erreur d\'accès à la caméra';
    
    if (err.name === 'NotAllowedError' || err.message?.includes('Permission')) {
      errorMessage = 'Permission caméra refusée. Autorisez l\'accès dans les paramètres.';
    } else if (err.name === 'NotFoundError') {
      errorMessage = 'Aucune caméra détectée.';
    } else if (err.name === 'NotReadableError') {
      errorMessage = 'Caméra utilisée par une autre application.';
    }
    
    setCameraError(errorMessage);
    setCameraReady(false);
  };

  const onScanSuccess = useCallback(async (decodedText) => {
    if (isProcessingRef.current || validating) return;
    isProcessingRef.current = true;

    if (navigator.vibrate) {
      navigator.vibrate(100);
    }

    let ticketNumber = null;
    let eventId = null;

    try {
      const data = JSON.parse(decodedText);
      ticketNumber = data.ticketNumber || data.ticket || data.id;
      eventId = data.eventId;
    } catch {
      if (decodedText.startsWith('TKT-')) {
        ticketNumber = decodedText;
      } else if (decodedText.includes('TKT-')) {
        const match = decodedText.match(/TKT-[A-Z0-9]+/);
        if (match) ticketNumber = match[0];
      }
    }

    if (ticketNumber) {
      // Check if ticket is for the selected event
      if (eventId && selectedEvent && eventId !== selectedEvent.id) {
        playErrorSound();
        if (navigator.vibrate) {
          navigator.vibrate([200, 50, 200]);
        }
        toast.error('❌ Ce billet n\'est pas pour cet événement!');
        setValidationResult({
          valid: false,
          message: 'Ce billet est pour un autre événement',
          errorType: 'WRONG_EVENT',
          ticketNumber: ticketNumber
        });
        setScanStats(prev => ({ ...prev, invalid: prev.invalid + 1, total: prev.total + 1 }));
        isProcessingRef.current = false;
        
        // Quick mode: auto return after 2 seconds
        if (quickMode) {
          quickModeTimeoutRef.current = setTimeout(() => {
            resetScanner();
          }, 2000);
        }
        return;
      }
      
      await validateTicket(ticketNumber);
    } else {
      playErrorSound();
      toast.error('QR code non reconnu');
      isProcessingRef.current = false;
    }
  }, [validating, selectedEvent, quickMode]);

  const onScanError = useCallback((errorMessage) => {
    // Silently ignore scanning errors
  }, []);

  const validateTicket = async (ticketNumber) => {
    setValidating(true);
    setValidationResult(null);

    // Keep scanner running for quick mode, just pause processing
    
    try {
      const response = await api.post('/tickets/validate', { 
        ticketNumber,
        eventId: selectedEvent?.id 
      });
      
      if (response.data.valid) {
        // Success feedback
        playSuccessSound();
        if (navigator.vibrate) {
          navigator.vibrate([50, 30, 50]);
        }
        toast.success('✅ Entrée validée!');
        setScanStats(prev => ({ ...prev, valid: prev.valid + 1, total: prev.total + 1 }));
      } else {
        // Error feedback
        playErrorSound();
        if (navigator.vibrate) {
          navigator.vibrate([200, 50, 200]);
        }
        toast.error('❌ ' + (response.data.message || 'Billet invalide'));
        setScanStats(prev => ({ ...prev, invalid: prev.invalid + 1, total: prev.total + 1 }));
      }
      
      setValidationResult(response.data);
      
      // Quick mode: auto return after delay
      if (quickMode) {
        const delay = response.data.valid ? 1500 : 2500;
        quickModeTimeoutRef.current = setTimeout(() => {
          resetScanner();
        }, delay);
      }
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur de validation';
      const isNotFound = error.response?.status === 404;
      
      // Error feedback
      playErrorSound();
      if (navigator.vibrate) {
        navigator.vibrate([200, 50, 200]);
      }
      
      toast.error(isNotFound ? '❌ Billet inexistant!' : '❌ ' + errorMessage);
      setScanStats(prev => ({ ...prev, invalid: prev.invalid + 1, total: prev.total + 1 }));
      
      setValidationResult({
        valid: false,
        message: isNotFound ? 'Ce numéro de billet n\'existe pas.' : errorMessage,
        errorType: isNotFound ? 'NOT_FOUND' : 'ERROR',
        ticketNumber: ticketNumber
      });
      
      // Quick mode: auto return
      if (quickMode) {
        quickModeTimeoutRef.current = setTimeout(() => {
          resetScanner();
        }, 2500);
      }
    } finally {
      setValidating(false);
      isProcessingRef.current = false;
    }
  };

  const handleManualValidation = (e) => {
    e.preventDefault();
    const ticket = manualTicketNumber.trim().toUpperCase();
    if (ticket) {
      validateTicket(ticket);
    }
  };

  const resetScanner = async () => {
    if (quickModeTimeoutRef.current) {
      clearTimeout(quickModeTimeoutRef.current);
    }
    
    // Reset states - useEffect will handle camera restart
    setValidationResult(null);
    setManualTicketNumber('');
    isProcessingRef.current = false;
    
    // Don't manually start scanner here - useEffect will do it when validationResult becomes null
  };

  const switchCamera = async () => {
    if (cameras.length < 2) return;
    await stopScanner();
    const currentIndex = cameras.findIndex(c => c.id === selectedCamera);
    const nextIndex = (currentIndex + 1) % cameras.length;
    setSelectedCamera(cameras[nextIndex].id);
  };

  const selectEvent = (event) => {
    setSelectedEvent(event);
    setScanStats({ valid: 0, invalid: 0, total: 0 });
  };

  const changeEvent = async () => {
    await stopScanner();
    setSelectedEvent(null);
    setValidationResult(null);
    setScanStats({ valid: 0, invalid: 0, total: 0 });
    setCameraReady(false);
  };

  const artist = tenant?.artist;

  // Event Selection Screen
  if (!selectedEvent) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 glass border-b border-border">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate(`/${artistSlug}/management`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div className="flex items-center gap-2">
              <SenArtisteLogo className="w-6 h-6" />
              <span className="font-syne font-bold">Scanner</span>
            </div>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </header>

        <main className="max-w-2xl mx-auto p-4 md:p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-syne text-2xl md:text-3xl font-bold text-center mb-2">
              Contrôle d'Accès
            </h1>
            <p className="text-center text-muted-foreground mb-6">
              Sélectionnez l'événement à contrôler
            </p>

            {loadingEvents ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : events.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-8 text-center">
                  <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Aucun événement</h3>
                  <p className="text-muted-foreground">
                    Aucun événement programmé pour le moment.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {events.map((event) => {
                  const eventDate = new Date(event.date || event.dateTime);
                  const now = new Date();
                  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
                  const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
                  const isToday = eventDate >= todayStart && eventDate <= todayEnd;
                  const canOpen = user?.role !== 'SCANNER' || isToday;
                  
                  return (
                  <motion.div
                    key={event.id}
                    whileHover={canOpen ? { scale: 1.02 } : {}}
                    whileTap={canOpen ? { scale: 0.98 } : {}}
                  >
                    <Card 
                      className={`glass-card transition-colors ${canOpen ? 'cursor-pointer hover:border-primary' : 'opacity-60 cursor-not-allowed'}`}
                      onClick={() => {
                        if (canOpen) {
                          selectEvent(event);
                        } else {
                          toast.error('Vous ne pouvez scanner que les événements du jour');
                        }
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0 ${isToday ? 'bg-green-500/20' : 'bg-primary/20'}`}>
                            <Ticket className={`w-8 h-8 ${isToday ? 'text-green-500' : 'text-primary'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg truncate">{event.title}</h3>
                              {isToday && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-600 whitespace-nowrap">
                                  Aujourd'hui
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {eventDate.toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {event.ticketsSold || 0} vendus
                              </span>
                            </div>
                          </div>
                          {canOpen ? (
                            <ChevronDown className="w-5 h-5 text-muted-foreground -rotate-90" />
                          ) : (
                            <span className="text-xs text-muted-foreground">🔒</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </main>
      </div>
    );
  }

  // Scanner Screen
  return (
    <div className="min-h-screen bg-background">
      {/* Header with event info */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={changeEvent}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Changer
            </Button>
            
            <div className="text-center flex-1 mx-2">
              <p className="font-semibold text-sm truncate">{selectedEvent.title}</p>
              <p className="text-xs text-muted-foreground">
                {new Date(selectedEvent.date).toLocaleDateString('fr-FR')}
              </p>
            </div>
            
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Stats bar */}
          <div className="flex justify-center gap-4 mt-2 text-xs">
            <span className="flex items-center gap-1 text-green-600">
              <Check className="w-3 h-3" /> {scanStats.valid} validés
            </span>
            <span className="flex items-center gap-1 text-red-600">
              <X className="w-3 h-3" /> {scanStats.invalid} refusés
            </span>
            <span className="text-muted-foreground">
              Total: {scanStats.total}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-3 space-y-3">
        {/* Quick Mode Toggle */}
        <div className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2">
          <span className="text-sm font-medium">Mode rapide</span>
          <Button
            variant={quickMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => setQuickMode(!quickMode)}
          >
            {quickMode ? 'Activé' : 'Désactivé'}
          </Button>
        </div>

        {/* Mode Selector */}
        <div className="flex gap-2">
          <Button
            variant={scanMode === 'camera' ? 'default' : 'outline'}
            onClick={async () => { 
              setValidationResult(null); 
              setScanMode('camera'); 
            }}
            className="flex-1"
            size="sm"
          >
            <Camera className="w-4 h-4 mr-2" />
            Caméra
          </Button>
          <Button
            variant={scanMode === 'manual' ? 'default' : 'outline'}
            onClick={async () => { 
              await stopScanner(); 
              setValidationResult(null); 
              setScanMode('manual'); 
            }}
            className="flex-1"
            size="sm"
          >
            <Keyboard className="w-4 h-4 mr-2" />
            Manuel
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {/* Camera Scanner */}
          {scanMode === 'camera' && !validationResult && (
            <motion.div
              key="camera"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  {cameraError ? (
                    <div className="p-6 text-center">
                      <VideoOff className="w-12 h-12 mx-auto text-destructive mb-3" />
                      <p className="text-sm text-muted-foreground mb-3">{cameraError}</p>
                      <div className="flex gap-2 justify-center">
                        <Button size="sm" onClick={() => { setCameraError(null); startScanner(); }}>
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Réessayer
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setScanMode('manual')}>
                          Manuel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      {!cameraReady && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                          <RefreshCw className="w-8 h-8 animate-spin text-white" />
                        </div>
                      )}
                      
                      <div id="qr-reader-container" className="w-full bg-black" style={{ minHeight: '300px' }} />
                      
                      {cameraReady && (
                        <>
                          {/* Scan overlay */}
                          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                            <div className="w-56 h-56 relative">
                              <div className="absolute top-0 left-0 w-6 h-6 border-l-4 border-t-4 border-primary" />
                              <div className="absolute top-0 right-0 w-6 h-6 border-r-4 border-t-4 border-primary" />
                              <div className="absolute bottom-0 left-0 w-6 h-6 border-l-4 border-b-4 border-primary" />
                              <div className="absolute bottom-0 right-0 w-6 h-6 border-r-4 border-b-4 border-primary" />
                              <motion.div 
                                className="absolute left-0 right-0 h-0.5 bg-primary/80"
                                animate={{ top: ['0%', '100%', '0%'] }}
                                transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                              />
                            </div>
                          </div>
                          
                          {cameras.length > 1 && (
                            <Button 
                              size="sm" 
                              variant="secondary"
                              onClick={switchCamera}
                              className="absolute bottom-2 right-2 bg-black/50"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  
                  {cameraReady && (
                    <div className="p-2 text-center border-t text-xs text-muted-foreground">
                      <Smartphone className="w-3 h-3 inline mr-1" />
                      Détection automatique
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Manual Input */}
          {scanMode === 'manual' && !validationResult && (
            <motion.div
              key="manual"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Card>
                <CardContent className="p-4">
                  <form onSubmit={handleManualValidation} className="space-y-3">
                    <Input
                      value={manualTicketNumber}
                      onChange={(e) => setManualTicketNumber(e.target.value.toUpperCase())}
                      placeholder="TKT-XXXXXXXX"
                      className="text-center font-mono text-lg h-12"
                      autoFocus
                    />
                    <Button 
                      type="submit" 
                      className="w-full h-11"
                      disabled={validating || !manualTicketNumber.trim()}
                    >
                      {validating ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <QrCode className="w-4 h-4 mr-2" />
                      )}
                      Valider
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Validation Result - Compact for quick scanning */}
          {validationResult && (
            <motion.div
              key="result"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Card 
                className={`overflow-hidden border-4 ${
                  validationResult.valid 
                    ? 'border-green-500 bg-green-500/10' 
                    : 'border-red-500 bg-red-500/10'
                }`}
              >
                <CardContent className="p-4 text-center">
                  {/* Big Status Icon */}
                  <motion.div 
                    className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-3 ${
                      validationResult.valid ? 'bg-green-500' : 'bg-red-500'
                    }`}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', duration: 0.3 }}
                  >
                    {validationResult.valid ? (
                      <Check className="w-12 h-12 text-white" strokeWidth={3} />
                    ) : (
                      <X className="w-12 h-12 text-white" strokeWidth={3} />
                    )}
                  </motion.div>
                  
                  {/* Status Text */}
                  <h2 className={`font-syne text-2xl font-bold mb-1 ${
                    validationResult.valid ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {validationResult.valid ? '✅ ENTRÉE OK' : '❌ REFUSÉ'}
                  </h2>
                  
                  <p className={`text-sm mb-3 ${
                    validationResult.valid ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {validationResult.valid 
                      ? validationResult.ticket?.buyerName || 'Billet validé'
                      : validationResult.errorType === 'ALREADY_USED' 
                        ? '⚠️ Déjà scanné!'
                        : validationResult.errorType === 'UNKNOWN_TICKET' || validationResult.errorType === 'NOT_FOUND'
                          ? '❓ Billet inconnu!'
                          : validationResult.errorType === 'WRONG_EVENT'
                            ? '🎫 Mauvais événement!'
                            : validationResult.errorType === 'CANCELLED'
                              ? '🚫 Billet annulé!'
                              : validationResult.message
                    }
                  </p>
                  
                  {/* Ticket number */}
                  {(validationResult.ticket?.ticketNumber || validationResult.ticketNumber) && (
                    <p className="font-mono text-sm text-muted-foreground mb-3">
                      {validationResult.ticket?.ticketNumber || validationResult.ticketNumber}
                    </p>
                  )}
                  
                  {/* Quick action button */}
                  <Button 
                    onClick={resetScanner} 
                    size="lg"
                    className="w-full h-12"
                  >
                    <QrCode className="w-5 h-5 mr-2" />
                    Suivant
                    {quickMode && (
                      <span className="ml-2 text-xs opacity-70">
                        (auto {validationResult.valid ? '1.5s' : '2.5s'})
                      </span>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default TicketScanner;
